import React from "react";

const RepositoryDoc = () => {
  return (
    <section className="mb-12 text-slate-300">
      {/* Title */}
      <h2 className="text-3xl font-bold text-white mb-6">
        Repository Layer — Usage Guide
      </h2>

      {/* Intro */}
      <p className="mb-6">
        The repository layer in AxisNet provides a clean, testable, and reusable
        abstraction over Entity Framework Core. It eliminates repetitive data
        access logic and ensures consistent querying behavior across all
        features. Below is a detailed explanation of how repositories are used
        and what responsibilities each built-in method fulfills.
      </p>

      {/* Usage Section */}
      <h3 className="text-2xl font-semibold text-white mb-4 mt-8">
        How Repositories Are Used
      </h3>

      <p className="mb-4">
        The repository layer standardizes data access operations and ensures
        consistency across the entire application. Instead of writing raw EF
        Core queries inside services or handlers, AxisNet uses repositories to
        provide a unified and extensible data-access API.
      </p>

      <ul className="space-y-3 bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <li>
          <strong className="text-emerald-400">• CRUD Operations</strong> —
          Centralized handling of add, update, delete, restore, and retrieval
          logic without repeating EF Core boilerplate.
        </li>

        <li>
          <strong className="text-emerald-400">• Query Composition</strong> —
          Repositories support predicates, ordering, includes, pagination,
          filters, and dynamic sorting through a unified method set.
        </li>

        <li>
          <strong className="text-emerald-400">• Soft Delete Awareness</strong>{" "}
          — All methods implicitly respect soft delete rules unless explicitly
          overridden using <em>withDeleted</em>.
        </li>

        <li>
          <strong className="text-emerald-400">• Dynamic Filtering</strong> —
          Built-in support for dynamic filtering using <em>Filter</em>,
          <em> Sort</em>, and <em>FilterOperator</em> without manually composing
          LINQ queries.
        </li>

        <li>
          <strong className="text-emerald-400">• Pagination Support</strong> —
          All list operations return a unified <em>Paginate&lt;T&gt;</em> model
          for consistent API responses.
        </li>

        <li>
          <strong className="text-emerald-400">• Eager Loading</strong> —
          Supports relational graph loading through Include / ThenInclude
          expressions while keeping EF Core details hidden behind abstractions.
        </li>

        <li>
          <strong className="text-emerald-400">• Tracking Control</strong> —
          Query tracking can be enabled or disabled to optimize performance
          depending on the scenario.
        </li>

        <li>
          <strong className="text-emerald-400">• Batch Operations</strong> —
          Range methods allow adding, updating, deleting, or restoring
          collections of entities efficiently.
        </li>

        <li>
          <strong className="text-emerald-400">• Testability</strong> —
          Repositories are interface-based, making it easy to mock them during
          unit tests without requiring a real database.
        </li>

        <li>
          <strong className="text-emerald-400">
            • Clean Architecture Alignment
          </strong>{" "}
          — Business logic never interacts directly with EF Core. Persistence
          concerns remain isolated and replaceable.
        </li>
      </ul>

      {/* Individual Responsibilities */}
      <h3 className="text-2xl font-semibold text-white mb-4 mt-10">
        Method Responsibilities
      </h3>

      <ul className="space-y-3 bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <li>
          <strong className="text-emerald-400">AnyAsync()</strong> — Checks
          whether any record exists matching a given condition. Ideal for
          validation scenarios (e.g., checking if email or username already
          exists).
        </li>

        <li>
          <strong className="text-emerald-400">CountAsync()</strong> — Returns
          the number of records matching filter conditions without loading them
          into memory. Frequently used for dashboard counters or pagination.
        </li>

        <li>
          <strong className="text-emerald-400">GetByIdAsync()</strong> —
          Retrieves a single entity by its identifier. Supports include
          expressions and soft-delete overrides.
        </li>

        <li>
          <strong className="text-emerald-400">GetSingleAsync()</strong> —
          Retrieves one record matching a predicate. Useful for authentication,
          email lookups, or configuration queries.
        </li>

        <li>
          <strong className="text-emerald-400">GetAsync()</strong> — Returns a
          paginated list of entities, supporting dynamic filtering, sorting,
          includes, and ordering. Commonly used for API list endpoints.
        </li>

        <li>
          <strong className="text-emerald-400">AddAsync()</strong> — Inserts a
          new entity and saves changes. Used for create operations across all
          aggregates.
        </li>

        <li>
          <strong className="text-emerald-400">AddRangeAsync()</strong> —
          Inserts multiple entities in a single operation, improving performance
          for bulk inserts.
        </li>

        <li>
          <strong className="text-emerald-400">UpdateAsync()</strong> — Updates
          an existing record and automatically maintains the UpdatedAt audit
          field.
        </li>

        <li>
          <strong className="text-emerald-400">UpdateRangeAsync()</strong> —
          Updates multiple records in a single batch.
        </li>

        <li>
          <strong className="text-emerald-400">DeleteAsync()</strong> — Soft
          deletes an entity by default, but can permanently delete when
          <em>permanent</em> is set to true.
        </li>

        <li>
          <strong className="text-emerald-400">DeleteRangeAsync()</strong> —
          Performs bulk soft or permanent deletion across multiple records.
        </li>

        <li>
          <strong className="text-emerald-400">RestoreAsync()</strong> —
          Restores a previously soft-deleted entity by clearing deletion audit
          fields.
        </li>

        <li>
          <strong className="text-emerald-400">RestoreRangeAsync()</strong> —
          Restores multiple entities in a batch operation.
        </li>
      </ul>

      {/* Usage Flow Section */}
      <h3 className="text-2xl font-semibold text-white mb-4 mt-10">
        Typical Repository Usage Flow
      </h3>

      <ul className="space-y-3 bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <li>
          <strong className="text-emerald-400">
            1. Define a repository interface{" "}
          </strong>
          (e.g., IUserRepository) extending the base repository contract.
        </li>

        <li>
          <strong className="text-emerald-400">
            2. Implement it using EfRepositoryBase
          </strong>{" "}
          to inherit all core behaviors automatically.
        </li>

        <li>
          <strong className="text-emerald-400">
            3. Register the repository in Dependency Injection
          </strong>{" "}
          so application services and handlers can consume it.
        </li>

        <li>
          <strong className="text-emerald-400">
            4. Access repository methods inside service or handler classes
          </strong>{" "}
          to perform all data operations.
        </li>

        <li>
          <strong className="text-emerald-400">
            5. Return structured responses (e.g., Paginate&lt;T&gt;)
          </strong>{" "}
          to the API layer for consistent frontend consumption.
        </li>
      </ul>

      {/* Real-World Example Usage */}
      <h3 className="text-2xl font-semibold text-white mb-4 mt-10">
        Creating & Using Custom Repositories
      </h3>

      <p className="mb-4">
        AxisNet allows each feature (Users, Roles, Products, Categories, etc.)
        to have its own repository interface while still inheriting all base
        repository functionality. This keeps your code clean, modular, and
        aligned with Clean Architecture rules.
      </p>

      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg space-y-6">
        {/* Step 1 */}
        <div>
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            1. Define a Feature-Specific Repository Interface
          </h4>
          <p className="mb-2">
            You extend{" "}
            <span className="text-emerald-400 font-semibold">
              IAsyncRepository
            </span>
            to add entity-specific query methods.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`public interface IUserRepository : IAsyncRepository<User, Guid>
{
    Task<User?> GetByEmailAsync(string email);
}`}
          </pre>
        </div>

        {/* Step 2 */}
        <div>
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            2. Implement It Using EfRepositoryBase
          </h4>
          <p className="mb-2">
            Custom repositories inherit all CRUD, filtering, pagination, and
            soft delete behavior from <strong>EfRepositoryBase</strong>.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`public class UserRepository : EfRepositoryBase<User, Guid, AppDbContext>, IUserRepository
{
    public UserRepository(AppDbContext context) : base(context) {}

    public async Task<User?> GetByEmailAsync(string email)
        => await GetSingleAsync(predicate: x => x.Email == email);
}`}
          </pre>
        </div>

        {/* Step 3 */}
        <div>
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            3. Register in Dependency Injection
          </h4>
          <p className="mb-2">
            After adding the repository, register it so the application layer
            can consume it.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`services.AddScoped<IUserRepository, UserRepository>();`}
          </pre>
        </div>

        {/* Step 4 */}
        <div>
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            4. Inject & Use in Handlers / Services
          </h4>

          <p className="mb-2">
            Repositories are consumed inside CQRS handlers or domain services,
            never directly in controllers.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`public class GetUserByEmailHandler : IRequestHandler<GetUserByEmailQuery, UserDto>
{
    private readonly IUserRepository _userRepository;

    public GetUserByEmailHandler(IUserRepository userRepository)
        => _userRepository = userRepository;

    public async Task<UserDto> Handle(GetUserByEmailQuery request, CancellationToken ct)
    {
        var user = await _userRepository.GetByEmailAsync(request.Email)
            ?? throw new NotFoundException("User not found");

        return new UserDto(user.Id, user.Email, user.FullName);
    }
}`}
          </pre>
        </div>

        {/* Step 5 */}
        <div>
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            5. Example: Fetching Paginated Users with Filters
          </h4>

          <p className="mb-2">
            Thanks to <strong>GetAsync()</strong>, filtering, pagination and
            ordering all work with a single call.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`var result = await _userRepository.GetAsync(
    page: 1,
    size: 20,
    predicate: x => x.IsActive,
    orderBy: q => q.OrderBy(x => x.CreatedAt),
    include: q => q.Include(x => x.Roles)
);`}
          </pre>
        </div>
      </div>

      {/* Responsibilities */}
      <h3 className="text-2xl font-semibold text-white mb-4 mt-10">
        What the Repository Layer Provides
      </h3>

      <ul className="space-y-3 bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <li>✓ A unified abstraction for EF Core data access</li>
        <li>✓ Centralized management of CRUD, filters, and pagination</li>
        <li>✓ Consistent application-wide querying behavior</li>
        <li>✓ Fully mockable and test-friendly repository structure</li>
        <li>✓ Strong alignment with Clean Architecture principles</li>
      </ul>
    </section>
  );
};

export default RepositoryDoc;
